import { commentSamples, emptyDm, emptyReply, emptyTrigger, fieldError, sampleConversations, sampleMedia, samplePerson, sampleTemplates, seedAutomationCopy, templateByKey, untitled } from './automationSamples';
import { LIBRARY } from './artSamples';

// The Direct Messages part of the in-memory core (contract 2.14.0, data/automations.openapi.json,
// status designing): channels and the official connection, the automations home, templates, the
// automations with their activation rules, media, results and the inbox. The rules the web relies
// on are kept: one connection per profile, plan gates, the account-wide limit, keyword conflicts,
// `expected_version`, a 30-day trash, the 24-hour window. `server` plays Meta's and the worker's
// part: the consent screen's outcomes, the seed of the mock scenarios and a comment on a post.
const HOUR = 3600e3;
const WINDOW_MS = 24 * HOUR;
const CAPABILITIES = ['comments', 'messages', 'insights'];
const SCOPES = { comments: 'instagram_business_manage_comments', messages: 'instagram_business_manage_messages', insights: 'instagram_business_manage_insights' };
// D11 in the card (not confirmed): which plan each option of the editor needs. Comes from core.
const PLAN_GATES = { activate: 'standard', media_scope_all: 'standard', media_scope_next: 'standard', follow_gate: 'pro', email_capture: 'pro', reminder: 'pro' };
const RANK = { free: 0, standard: 1, pro: 2 };
const ACTIVE_LIMIT = { free: 0, standard: 2, pro: null };
// D5 in the card: the Standard plan's text says 500; what the free plan keeps is not decided.
const CONTACT_LIMIT = { free: 25, standard: 500, pro: null };
const TRASH_DAYS = 30;
const SORTS = { '-updated': (a, b) => b.timestamps.updated.localeCompare(a.timestamps.updated), updated: (a, b) => a.timestamps.updated.localeCompare(b.timestamps.updated),
  name: (a, b) => a.name.localeCompare(b.name), '-name': (a, b) => b.name.localeCompare(a.name) };
const accents = value => (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const ago = hours => new Date(Date.now() - hours * HOUR).toISOString();
const emptyStats = () => ({ triggers: 0, contacts: 0, comment_replies_sent: 0, sends: 0, clicks: 0, ctr: null, emails: 0, reminders_sent: 0, failures: 0, timestamps: { last_triggered: null } });
const paged = (list, { page = 1, per_page: perPage = 20 } = {}) => {
  const number = Number(page);
  const size = Number(perPage);
  return { current_page: number, per_page: size, total: list.length, last_page: Math.max(1, Math.ceil(list.length / size)), data: list.slice((number - 1) * size, number * size) };
};
const truthy = value => value === true || value === 'true';
const specified = value => value !== undefined && value !== null && value !== '';

export const emptyDirectMessages = () => ({ connections: [], media: {}, automations: [], executions: [], conversations: [], messages: [], funnels: {}, contacts: {} });

export function createAutomationRoutes({ db, fail, nextId, now, clone, profiles, plan, assetUrl }) {
  const store = () => db().directMessages;
  const observed = () => ({ observed: now() });
  const profileOf = id => {
    if (!/^spr-[0-9a-z]{7,10}$/.test(id || '')) fail('validation_error', 422, {}, { profile_id: ['invalid'] });
    return profiles().find(item => item.id === id) || fail('not_found', 404);
  };
  const localeOf = id => profiles().find(item => item.id === id)?.locale || 'pt_BR';
  const connectionOf = profileId => store().connections.find(item => item.profile_id === profileId) || null;
  const isConnected = (profileId, capability) => {
    const connection = connectionOf(profileId);
    return connection?.status === 'connected' && (!capability || connection.capabilities.includes(capability));
  };
  const needsConnection = (profileId, capability) => { if (!isConnected(profileId, capability)) fail('connection_required', 409); };

  const summary = connection => (connection ? { id: connection.id, status: connection.status, handle: connection.account?.handle ?? null, capabilities: connection.capabilities, missing_capabilities: connection.missing_capabilities } : null);
  const accountOf = profile => ({ handle: profile.handle, name: profile.name ?? null, picture: profile.picture ?? null, account_type: 'creator', followers_count: 48210, media_count: 312 });

  // ── Connection ──
  const openConnection = ({ profile_id: profileId, platform, force_reauth: reauth = false }) => {
    profileOf(profileId);
    if (platform !== 'instagram') fail('platform_not_supported', 422, {}, { platform: ['not supported'] });
    let connection = connectionOf(profileId);
    if (connection?.status === 'connected' && !reauth) fail('connection_already_active', 409);
    if (!connection) {
      connection = { id: nextId('scn'), profile_id: profileId, platform, status: 'pending', account: null, scopes: [], capabilities: [], missing_capabilities: [], authorization: null, last_error: null, conflict: null, version: 0,
        timestamps: { created: now(), updated: now(), connected: null, token_expires: null, last_synced: null, disconnected: null } };
      store().connections.push(connection);
    }
    // The state of the last answer, to go back to when the Meta dialog is left without a connection.
    connection.resume = connection.status === 'pending' ? connection.resume : connection.status;
    Object.assign(connection, { status: 'pending', last_error: null, conflict: null, version: connection.version + 1,
      authorization: { url: `https://www.instagram.com/oauth/authorize?client_id=mock&state=${connection.id}${reauth ? '&force_reauth=true' : ''}`, timestamps: { created: now(), expires: new Date(Date.now() + 600e3).toISOString() } } });
    connection.timestamps.updated = now();
    return connection;
  };
  const visible = ({ resume, ...connection }) => clone(connection);

  // ── Media ──
  const mediaOf = profileId => {
    if (!store().media[profileId]) {
      store().media[profileId] = sampleMedia().map(item => ({
        id: nextId('smd'), profile_id: profileId, format: item.format, caption: item.caption, permalink: `https://www.instagram.com/p/${nextId('ig').replace('-', '')}/`,
        preview: { url: item.url, timestamps: { expires: null } }, availability: 'available', like_count: item.likes, comments_count: item.comments, publication_id: null,
        timestamps: { published: ago(item.hoursAgo), expires: item.format === 'instagram::story' ? ago(item.hoursAgo - 24) : null, synced: now() },
      }));
    }
    return store().media[profileId];
  };
  const mediaView = item => ({ ...item, automations_count: store().automations.filter(a => !a.timestamps.deleted && a.status === 'active' && a.trigger.media_scope === 'selected' && a.trigger.media_ids.includes(item.id)).length });

  // ── Automations ──
  const statsOf = automation => {
    const stats = automation.stats;
    return { ...stats, ctr: stats.sends ? Math.round((stats.clicks / stats.sends) * 1000) / 1000 : null };
  };
  const view = automation => ({ ...clone(automation), stats: statsOf(automation) });
  const live = id => {
    if (!/^sau-[0-9a-z]{7,10}$/.test(id || '')) fail('not_found', 404);
    const automation = store().automations.find(item => item.id === id);
    if (!automation) fail('not_found', 404);
    return automation;
  };
  const untrashed = id => { const automation = live(id); if (automation.timestamps.deleted) fail('not_found', 404); return automation; };
  const quotaOf = () => {
    const sku = plan();
    const active = store().automations.filter(item => !item.timestamps.deleted && item.status === 'active').length;
    const limit = ACTIVE_LIMIT[sku];
    return { plan: sku, available: RANK[sku] >= RANK[PLAN_GATES.activate], active, limit, remaining: limit === null ? null : Math.max(0, limit - active) };
  };
  // The options an automation uses that sit above the current plan, field by field.
  const planErrors = (automation, locale) => {
    const sku = plan();
    const message = fieldError('plan', locale);
    const uses = [
      ['status', 'activate', true],
      ['trigger.media_scope', 'media_scope_all', automation.trigger.media_scope === 'all' && automation.trigger.channel !== 'direct_messages'],
      ['trigger.media_scope', 'media_scope_next', automation.trigger.media_scope === 'next'],
      ['dm.follow_gate.enabled', 'follow_gate', automation.dm.follow_gate.enabled],
      ['dm.email_capture.enabled', 'email_capture', automation.dm.email_capture.enabled],
      ['dm.reminder.enabled', 'reminder', automation.dm.reminder.enabled],
    ];
    return Object.fromEntries(uses.filter(([, gate, used]) => used && RANK[sku] < RANK[PLAN_GATES[gate]]).map(([field]) => [field, [message]]));
  };
  const LINK = /https:\/\/\S+/;
  const hasLink = message => message.buttons.length > 0 || LINK.test(message.text);
  // What a complete automation has; a draft may lack any of it (422 only on activation).
  const incomplete = (automation, locale) => {
    const errors = {};
    const miss = (field, key = 'required') => { errors[field] = [fieldError(key, locale)]; };
    const { trigger, dm, comment_reply: reply } = automation;
    if (trigger.match !== 'any' && !trigger.keywords.length) miss('trigger.keywords', 'keywords');
    if (trigger.channel !== 'direct_messages' && trigger.media_scope === 'selected' && !trigger.media_ids.length) miss('trigger.media_ids', 'media');
    if (reply.enabled && !reply.templates.length) miss('comment_reply.templates', 'templates');
    if (dm.opening.enabled) { if (!dm.opening.text.trim()) miss('dm.opening.text'); if (!dm.opening.button_label.trim()) miss('dm.opening.button_label'); }
    if (dm.follow_gate.enabled) ['text', 'button_label', 'not_following_text'].forEach(field => { if (!dm.follow_gate[field].trim()) miss(`dm.follow_gate.${field}`); });
    if (dm.email_capture.enabled) ['text', 'invalid_text'].forEach(field => { if (!dm.email_capture[field].trim()) miss(`dm.email_capture.${field}`); });
    if (!dm.delivery.text.trim()) miss('dm.delivery.text');
    else if (dm.delivery.buttons.length && dm.delivery.text.length > 640) miss('dm.delivery.text', 'tooLong');
    if (dm.reminder.enabled) {
      if (!dm.reminder.message.text.trim()) miss('dm.reminder.message.text');
      if (!hasLink(dm.delivery)) miss('dm.reminder.enabled', 'reminderLink');
      else if (!dm.opening.enabled && !dm.follow_gate.enabled && !dm.email_capture.enabled) miss('dm.reminder.enabled', 'reminderStep');
    }
    return errors;
  };
  const scopesOverlap = (a, b) => {
    if (a.trigger.media_scope === 'all' || b.trigger.media_scope === 'all') return a.trigger.media_scope === b.trigger.media_scope;
    return a.trigger.media_scope === 'next' ? b.trigger.media_scope === 'next' : a.trigger.media_ids.some(id => b.trigger.media_ids.includes(id));
  };
  const conflictOf = automation => {
    const words = automation.trigger.keywords.map(accents);
    const others = store().automations.filter(item => item.id !== automation.id && !item.timestamps.deleted && item.status === 'active' && item.profile_id === automation.profile_id && item.trigger.channel === automation.trigger.channel);
    return others.find(item => scopesOverlap(item, automation) && (automation.trigger.match === 'any' || item.trigger.match === 'any' || item.trigger.keywords.some(word => words.includes(accents(word)))));
  };
  // PATCH/POST `status: active`: plan and options, limit, connection, conflict, a complete configuration.
  const activate = automation => {
    const locale = localeOf(automation.profile_id);
    const quota = quotaOf();
    const gated = planErrors(automation, locale);
    if (Object.keys(gated).length) fail('plan_upgrade_required', 403, {}, gated);
    if (automation.status !== 'active' && quota.limit !== null && quota.active >= quota.limit) fail('automation_limit_reached', 409);
    const needed = automation.trigger.channel === 'direct_messages' ? ['messages'] : ['comments', 'messages'];
    if (!needed.every(capability => isConnected(automation.profile_id, capability))) fail('connection_required', 409);
    const conflict = conflictOf(automation);
    if (conflict) fail('keyword_conflict', 409, {}, { 'trigger.keywords': [fieldError('conflict', locale, { name: conflict.name })] });
    const errors = incomplete(automation, locale);
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    Object.assign(automation, { status: 'active', paused_reason: null });
    automation.timestamps.activated = now();
  };
  const checkConfig = ({ trigger, comment_reply: reply, dm }, locale) => {
    const errors = {};
    const bad = (field, key) => { errors[field] = [fieldError(key, locale)]; };
    const links = (path, message) => message?.buttons?.forEach((button, index) => { if (!/^https:\/\//.test(button.url || '')) bad(`${path}.buttons.${index}.url`, 'https'); });
    links('dm.delivery', dm?.delivery);
    links('dm.reminder.message', dm?.reminder?.message);
    if (reply && reply.enabled && trigger && trigger.channel !== 'comments') bad('comment_reply.enabled', 'required');
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
  };
  const touchAutomation = automation => { automation.version += 1; automation.timestamps.updated = now(); return automation; };

  // ── Inbox ──
  const windowOf = lastInbound => {
    const closes = lastInbound ? new Date(new Date(lastInbound).getTime() + WINDOW_MS) : null;
    return { open: !!closes && closes.getTime() > Date.now(), timestamps: { closes: closes ? closes.toISOString() : null } };
  };
  const preview = message => (message ? { direction: message.direction, text: message.text ? message.text.slice(0, 200) : null, timestamps: clone(message.timestamps) } : null);
  const conversationView = conversation => {
    const messages = store().messages.filter(item => item.conversation_id === conversation.id);
    const lastInbound = messages.filter(item => item.direction === 'inbound').map(item => item.timestamps.sent).sort().pop() || null;
    const last = messages.map(item => item).sort((a, b) => a.timestamps.sent.localeCompare(b.timestamps.sent)).pop() || null;
    return { id: conversation.id, profile_id: conversation.profile_id, participant: clone(conversation.participant), last_message: preview(last), unread_count: conversation.unread_count, window: windowOf(lastInbound),
      timestamps: { created: conversation.created, updated: last?.timestamps.sent || conversation.created, last_inbound: lastInbound } };
  };
  const conversationOf = id => {
    if (!/^scv-[0-9a-z]{7,10}$/.test(id || '')) fail('not_found', 404);
    return store().conversations.find(item => item.id === id) || fail('not_found', 404);
  };
  const addMessage = (conversation, { direction, origin, text = null, attachments = [], story = null, automationId = null, status = null, sent = now() }) => {
    const message = { id: nextId('sdm'), conversation_id: conversation.id, direction, origin, text, attachments, reply_to_story: story, automation_id: automationId, status: status || (direction === 'inbound' ? 'received' : 'sent'), error: null,
      timestamps: { sent, read: null } };
    store().messages.push(message);
    return message;
  };
  // The contact of a person, one per profile.
  const contactOf = (profileId, person) => {
    const key = `${profileId}:${person.handle}`;
    if (!store().contacts[key]) store().contacts[key] = { id: nextId('sct'), handle: person.handle, name: person.name, picture: null, email: null };
    return store().contacts[key];
  };

  const stepsOf = automation => {
    const { dm, comment_reply: reply } = automation;
    return [reply.enabled && 'comment_reply', dm.opening.enabled && 'opening', dm.follow_gate.enabled && 'follow_gate', dm.email_capture.enabled && 'email_capture', 'delivery', dm.reminder.enabled && 'reminder'].filter(Boolean);
  };
  const nextStepsFor = profileId => {
    const connection = connectionOf(profileId);
    const steps = [];
    const add = (kind, extra = {}) => steps.push({ kind, template_key: null, plan: null, ...extra });
    const ownAutomations = store().automations.filter(item => item.profile_id === profileId && !item.timestamps.deleted);
    const quota = quotaOf();
    if (!connection || connection.status === 'disconnected' || (connection.status === 'pending' && !connection.account)) add('connect_account');
    else if (connection.status === 'reauthorization_required') add('reconnect_account');
    else if (connection.missing_capabilities.length) add('grant_permissions');
    if (!ownAutomations.length) add('create_automation');
    const used = new Set(ownAutomations.map(item => item.template_key));
    const template = ['comment_link', 'dm_reply', 'story_leads', 'comment_followers'].find(key => !used.has(key));
    if (template) add('use_template', { template_key: template });
    if (!quota.available) add('upgrade_plan', { plan: 'standard' });
    else if (quota.limit !== null && quota.remaining === 0) add('upgrade_plan', { plan: 'pro' });
    const contacts = contactsOf(profileId);
    const limit = CONTACT_LIMIT[plan()];
    if (limit !== null && contacts >= limit * 0.8) add('contacts_limit_near');
    return steps;
  };
  const contactsOf = profileId => (store().contactCounts?.[profileId] ?? 0) + Object.keys(store().contacts).filter(key => key.startsWith(`${profileId}:`)).length;

  const routes = {
    // 01 — Channels and connection
    platforms: ({ profile_id: profileId } = {}) => {
      if (specified(profileId)) profileOf(profileId);
      const options = [['instagram', 'available'], ['tiktok', 'coming_soon'], ['youtube', 'coming_soon']];
      return { data: options.map(([platform, status]) => ({ platform, status, connection: profileId && platform === 'instagram' ? summary(connectionOf(profileId)) : null })), timestamps: observed() };
    },
    connections: ({ profile_id: profileId } = {}) => {
      if (specified(profileId)) profileOf(profileId);
      return { data: store().connections.filter(item => !profileId || item.profile_id === profileId).map(visible), timestamps: observed() };
    },
    createConnection: body => visible(openConnection(body)),
    connection: id => visible(store().connections.find(item => item.id === id) || fail('not_found', 404)),
    disconnect: id => {
      const connection = store().connections.find(item => item.id === id) || fail('not_found', 404);
      if (connection.status === 'disconnected') return visible(connection);
      Object.assign(connection, { status: 'disconnected', authorization: null, capabilities: [], scopes: [], missing_capabilities: [], version: connection.version + 1 });
      Object.assign(connection.timestamps, { updated: now(), disconnected: now(), token_expires: null });
      store().automations.filter(item => item.profile_id === connection.profile_id && item.status === 'active' && !item.timestamps.deleted).forEach(item => { Object.assign(item, { status: 'paused', paused_reason: 'connection_unavailable' }); touchAutomation(item); });
      return visible(connection);
    },

    // 02 — Home and templates
    automationsOverview: ({ profile_id: profileId }) => {
      profileOf(profileId);
      const connection = connectionOf(profileId);
      const conversations = store().conversations.filter(item => item.profile_id === profileId);
      return {
        profile_id: profileId,
        connected_channels: connection?.status === 'connected' ? 1 : 0,
        connection: summary(connection),
        inbox: { unread_conversations: conversations.filter(item => item.unread_count > 0).length, unread_messages: conversations.reduce((total, item) => total + item.unread_count, 0) },
        quota: quotaOf(),
        plan_gates: PLAN_GATES,
        contacts: { total: contactsOf(profileId), limit: CONTACT_LIMIT[plan()] },
        next_steps: nextStepsFor(profileId),
        timestamps: observed(),
      };
    },
    automationTemplates: ({ profile_id: profileId, q, objective, channel } = {}) => {
      if (specified(profileId)) profileOf(profileId);
      const words = accents(q);
      const data = sampleTemplates(localeOf(profileId)).filter(item => (!objective || item.objective === objective) && (!channel || item.channel === channel)
        && (!words || accents(`${item.title} ${item.summary}`).includes(words))).sort((a, b) => Number(b.recommended) - Number(a.recommended));
      return { data, timestamps: observed() };
    },

    // 03 — Automations
    automations: ({ profile_id: profileId, status, q, channel, trashed, sort = '-updated', page, per_page: perPage } = {}) => {
      profileOf(profileId);
      const words = accents(q);
      const list = store().automations.filter(item => item.profile_id === profileId && (truthy(trashed) ? !!item.timestamps.deleted : !item.timestamps.deleted)
        && (!status || item.status === status) && (!channel || item.trigger.channel === channel)
        && (!words || accents(`${item.name} ${item.trigger.keywords.join(' ')}`).includes(words))).sort(SORTS[sort] || SORTS['-updated']);
      return { ...paged(list, { page, per_page: perPage }), data: paged(list, { page, per_page: perPage }).data.map(view), quota: quotaOf() };
    },
    createAutomation: body => {
      const { profile_id: profileId, template_key: templateKey, name, status = 'draft', trigger, comment_reply: reply, dm } = body;
      profileOf(profileId);
      const locale = localeOf(profileId);
      const template = templateKey ? templateByKey(templateKey, locale) : null;
      if (templateKey && !template) fail('validation_error', 422, {}, { template_key: ['unknown'] });
      checkConfig(body, locale);
      const automation = { id: nextId('sau'), profile_id: profileId, template_key: templateKey || null, name: name || template?.draft.name || untitled(locale), status: 'draft', paused_reason: null,
        trigger: clone(trigger || template?.draft.trigger || emptyTrigger()), comment_reply: clone(reply || template?.draft.comment_reply || emptyReply()), dm: clone(dm || template?.draft.dm || emptyDm()),
        stats: emptyStats(), version: 1, timestamps: { created: now(), updated: now(), activated: null, deleted: null } };
      if (status === 'active') activate(automation);
      store().automations.push(automation);
      return view(automation);
    },
    automation: id => view(untrashed(id)),
    updateAutomation: (id, { expected_version: version, status, ...fields }) => {
      const automation = untrashed(id);
      if (version !== automation.version) fail('version_conflict', 409, { current_version: automation.version });
      checkConfig({ trigger: fields.trigger || automation.trigger, comment_reply: fields.comment_reply || automation.comment_reply, dm: fields.dm }, localeOf(automation.profile_id));
      const next = { ...clone(automation), ...clone(fields) };
      if (status === 'active') activate(next);
      // A running automation cannot lose what makes it complete.
      else if (status !== 'paused' && automation.status === 'active') {
        const errors = incomplete(next, localeOf(automation.profile_id));
        if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      }
      if (status === 'paused') Object.assign(next, { status: 'paused', paused_reason: 'creator' });
      Object.assign(automation, next);
      return view(touchAutomation(automation));
    },
    deleteAutomation: id => {
      const automation = untrashed(id);
      if (automation.status === 'active') Object.assign(automation, { status: 'paused', paused_reason: 'creator' });
      automation.timestamps.deleted = now();
      touchAutomation(automation);
      return null;
    },
    restoreAutomation: id => {
      const automation = live(id);
      if (!automation.timestamps.deleted) return view(automation);
      if (Date.now() - new Date(automation.timestamps.deleted).getTime() > TRASH_DAYS * 24 * HOUR) fail('not_found', 404);
      automation.timestamps.deleted = null;
      Object.assign(automation, automation.timestamps.activated ? { status: 'paused', paused_reason: 'creator' } : { status: 'draft', paused_reason: null });
      return view(touchAutomation(automation));
    },
    media: ({ profile_id: profileId, channel, format, cursor, limit = 24 }) => {
      profileOf(profileId);
      needsConnection(profileId);
      const all = mediaOf(profileId).filter(item => (format ? item.format === format : channel === 'stories' ? item.format === 'instagram::story' : channel === 'comments' ? item.format !== 'instagram::story' : true) && item.availability === 'available');
      const start = cursor ? Number(cursor.split(':')[1]) : 0;
      const end = start + Number(limit);
      return { data: all.slice(start, end).map(mediaView), next_cursor: end < all.length ? `smc:${end}` : null, has_more: end < all.length, timestamps: observed() };
    },
    mediaItem: id => {
      const item = Object.values(store().media).flat().find(entry => entry.id === id);
      return item ? mediaView(item) : fail('not_found', 404);
    },

    // 04 — Results
    automationInsights: id => {
      const automation = untrashed(id);
      const funnel = store().funnels[id] || {};
      const stats = statsOf(automation);
      const steps = [];
      if (automation.trigger.channel !== 'direct_messages') steps.push({ kind: 'use_template', template_key: 'dm_reply', plan: null });
      if (plan() !== 'pro') steps.push({ kind: 'upgrade_plan', template_key: null, plan: 'pro' });
      return { automation_id: id, stats, funnel: stepsOf(automation).map(step => ({ step, sent: funnel[step]?.sent ?? 0, completed: funnel[step]?.completed ?? 0 })), next_steps: steps, timestamps: observed() };
    },
    automationExecutions: (id, { status, clicked, has_email: hasEmail, page, per_page: perPage } = {}) => {
      untrashed(id);
      const list = store().executions.filter(item => item.automation_id === id && (!status || item.status === status)
        && (!specified(clicked) || truthy(clicked) === !!item.timestamps.clicked) && (!specified(hasEmail) || truthy(hasEmail) === !!item.contact.email))
        .sort((a, b) => b.timestamps.triggered.localeCompare(a.timestamps.triggered));
      return paged(clone(list), { page, per_page: perPage });
    },

    // 05 — Inbox
    conversations: ({ profile_id: profileId, unread, page, per_page: perPage }) => {
      profileOf(profileId);
      needsConnection(profileId, 'messages');
      const list = store().conversations.filter(item => item.profile_id === profileId && (!truthy(unread) || item.unread_count > 0)).map(conversationView)
        .sort((a, b) => b.timestamps.updated.localeCompare(a.timestamps.updated));
      return paged(list, { page, per_page: perPage });
    },
    conversation: id => conversationView(conversationOf(id)),
    updateConversation: (id, body) => {
      const conversation = conversationOf(id);
      if (body?.read !== true) fail('validation_error', 422, {}, { read: ['must be true'] });
      conversation.unread_count = 0;
      return conversationView(conversation);
    },
    directMessages: (id, { before, limit = 30 } = {}) => {
      conversationOf(id);
      // Newest first; messages of the same instant keep the order they were written in.
      const all = store().messages.filter(item => item.conversation_id === id).sort((a, b) => a.timestamps.sent.localeCompare(b.timestamps.sent)).reverse();
      const start = before ? all.findIndex(item => item.id === before) + 1 : 0;
      const end = start + Number(limit);
      const data = clone(all.slice(start, end));
      return { data, next_cursor: end < all.length && data.length ? data[data.length - 1].id : null, has_more: end < all.length, timestamps: observed() };
    },
    sendDirectMessage: (id, { text, asset_id: assetId } = {}) => {
      const conversation = conversationOf(id);
      if (!text && !assetId) fail('validation_error', 422, {}, { text: ['required'] });
      needsConnection(conversation.profile_id, 'messages');
      if (!conversationView(conversation).window.open) fail('messaging_window_closed', 409);
      // The creator took over: what the automation still had to say in this conversation stops.
      store().executions.filter(item => item.contact.id === conversation.participant.contact_id && ['pending', 'running', 'awaiting_reply'].includes(item.status)).forEach(item => {
        item.status = 'cancelled';
        item.steps.filter(step => step.status === 'pending').forEach(step => { step.status = 'cancelled'; });
      });
      return clone(addMessage(conversation, { direction: 'outbound', origin: 'studio', text: text || null, attachments: assetId ? [{ type: 'image', url: assetUrl(assetId) }] : [] }));
    },
  };

  const storyPhoto = key => LIBRARY[key]?.url ?? null;
  // A person on a mock thread, with what they wrote.
  const seedConversation = (profileId, entry, photo) => {
    const person = samplePerson(entry.person);
    const contact = contactOf(profileId, person);
    const conversation = { id: nextId('scv'), profile_id: profileId, participant: { handle: person.handle, name: person.name, picture: null, contact_id: contact.id }, unread_count: entry.unread, created: ago(Math.max(...entry.thread.map(item => item.age)) + 1) };
    store().conversations.push(conversation);
    entry.thread.forEach(item => addMessage(conversation, {
      direction: item.from === 'contact' ? 'inbound' : 'outbound', origin: item.from, text: item.text, sent: ago(item.age),
      story: item.story ? { media_id: null, url: photo(item.story) } : null,
      automationId: item.from === 'automation' ? store().automations.find(a => a.profile_id === profileId && a.status === 'active')?.id ?? null : null,
    }));
    return conversation;
  };

  // A passage through an automation, as the worker leaves it.
  const seedExecution = (automation, index, comments, hoursAgo, outcome) => {
    const person = samplePerson(index);
    const contact = contactOf(automation.profile_id, person);
    if (outcome.email) contact.email = `${person.handle.replace(/[^a-z0-9]/g, '')}@email.com`;
    const steps = stepsOf(automation);
    const reached = outcome.reached ?? steps.length;
    const triggered = ago(hoursAgo);
    const sent = ago(hoursAgo - 0.02);
    const execution = { id: nextId('sae'), automation_id: automation.id, automation_version: automation.version, contact: clone(contact),
      media_id: automation.trigger.media_ids[0] ?? null, matched_keyword: automation.trigger.match === 'any' ? null : automation.trigger.keywords[index % automation.trigger.keywords.length], text: comments[index % comments.length],
      status: outcome.status, steps: steps.map((step, position) => {
        const status = position < reached ? 'completed' : position === reached ? outcome.current || 'sent' : 'pending';
        return { step, status, error: status === 'failed' ? { code: 'platform_error', message: fieldError('delivery', localeOf(automation.profile_id)), timestamps: { occurred: sent } } : null,
          timestamps: { sent: position <= reached ? sent : null, completed: position < reached ? sent : null } };
      }),
      timestamps: { triggered, clicked: outcome.clicked ? sent : null, completed: outcome.status === 'completed' ? sent : null } };
    store().executions.push(execution);
    return execution;
  };

  const server = {
    // The Meta dialog's outcome for an open authorization; answers what the callback redirects with.
    completeConnection: (id, outcome, { importInbox = true } = {}) => {
      const connection = store().connections.find(item => item.id === id) || fail('not_found', 404);
      const profile = profileOf(connection.profile_id);
      const settle = fields => {
        Object.assign(connection, { authorization: null, version: connection.version + 1, ...fields });
        connection.timestamps.updated = now();
        return { connection: visible(connection), result: connection.last_error ? (connection.last_error.code === 'access_denied' ? 'cancelled' : 'error') : 'connected', code: connection.last_error?.code ?? null };
      };
      const refused = (code, extra = {}) => settle({ status: connection.resume === 'connected' ? 'connected' : 'disconnected', last_error: { code, message: code, timestamps: { occurred: now() } }, ...extra });
      if (outcome === 'cancelled') return refused('access_denied');
      if (outcome === 'not_professional') return refused('account_not_professional');
      if (outcome === 'account_mismatch' || outcome === 'account_in_use') {
        const other = profiles().find(item => item.id !== connection.profile_id);
        return refused(outcome, { conflict: { code: outcome, account: { handle: outcome === 'account_in_use' && other ? other.handle : 'outra.conta', name: null }, own_profile_id: outcome === 'account_in_use' && other ? other.id : null } });
      }
      const missing = outcome === 'missing_messages' ? ['messages'] : [];
      const capabilities = CAPABILITIES.filter(item => !missing.includes(item));
      connection.timestamps.connected = now();
      connection.timestamps.last_synced = now();
      connection.timestamps.token_expires = new Date(Date.now() + 60 * 24 * HOUR).toISOString();
      connection.timestamps.disconnected = null;
      mediaOf(connection.profile_id);
      // Reconnecting brings back only what the system paused.
      if (!missing.length) store().automations.filter(item => item.profile_id === connection.profile_id && item.paused_reason === 'connection_unavailable' && !item.timestamps.deleted).forEach(item => { Object.assign(item, { status: 'active', paused_reason: null }); touchAutomation(item); });
      // The first connection imports the recent conversations.
      if (importInbox && !missing.length && !store().conversations.some(item => item.profile_id === connection.profile_id)) sampleConversations().slice(0, 2).forEach(entry => seedConversation(connection.profile_id, entry, storyPhoto));
      return settle({ status: 'connected', account: accountOf(profile), scopes: ['instagram_business_basic', ...capabilities.map(item => SCOPES[item])], capabilities, missing_capabilities: missing, last_error: null, conflict: null });
    },
    // The opening state of a mock scenario: `connected`, `reauth`, `missing`.
    seedDirectMessages: (profileId, mode = 'connected', { contacts = 128 } = {}) => {
      const profile = profileOf(profileId);
      const locale = profile.locale || 'pt_BR';
      openConnection({ profile_id: profileId, platform: 'instagram' });
      server.completeConnection(connectionOf(profileId).id, mode === 'missing' ? 'missing_messages' : 'connected', { importInbox: false });
      store().contactCounts = { ...(store().contactCounts || {}), [profileId]: contacts };
      if (mode === 'empty') return;
      const copy = seedAutomationCopy(locale);
      const media = mediaOf(profileId);
      const posts = media.filter(item => item.format !== 'instagram::story');
      const base = templateByKey('comment_link', locale).draft;
      const stamps = (createdHours, updatedHours, activated = true, deleted = null) => ({ created: ago(createdHours), updated: ago(updatedHours), activated: activated ? ago(createdHours - 1) : null, deleted: deleted ? ago(deleted) : null });
      const add = fields => {
        const automation = { id: nextId('sau'), profile_id: profileId, template_key: null, status: 'active', paused_reason: null, trigger: emptyTrigger(), comment_reply: emptyReply(), dm: emptyDm(), stats: emptyStats(), version: 1, ...fields };
        store().automations.push(automation);
        return automation;
      };
      const recipe = add({ template_key: 'comment_link', name: copy.recipe.name, trigger: { channel: 'comments', media_scope: 'selected', media_ids: [posts[0].id, posts[2].id], keywords: copy.recipe.keywords, match: 'word' }, comment_reply: base.comment_reply,
        dm: { ...base.dm, delivery: { text: base.dm.delivery.text, buttons: [{ title: templateByKey('comment_link', locale).draft.name.slice(0, 20), url: copy.recipe.url }] } },
        stats: { triggers: 38, contacts: 36, comment_replies_sent: 38, sends: 31, clicks: 19, ctr: null, emails: 0, reminders_sent: 0, failures: 1, timestamps: { last_triggered: ago(0.4) } }, timestamps: stamps(240, 6) });
      store().funnels[recipe.id] = { comment_reply: { sent: 38, completed: 38 }, opening: { sent: 36, completed: 31 }, delivery: { sent: 31, completed: 19 } };
      const comments = commentSamples(locale);
      for (let index = 0; index < 38; index += 1) {
        const outcome = index % 9 === 8 ? { status: 'failed', reached: 1, current: 'failed' } : index % 5 === 3 ? { status: 'awaiting_reply', reached: 1 } : index % 3 === 0 ? { status: 'completed', clicked: true } : { status: 'completed', reached: 2, current: 'sent', clicked: false };
        seedExecution(recipe, index, comments, 0.4 + index * 5.5, outcome);
      }
      const workout = add({ template_key: 'comment_link', name: copy.workout.name, trigger: { channel: 'comments', media_scope: 'selected', media_ids: [posts[3].id], keywords: copy.workout.keywords, match: 'word' }, comment_reply: base.comment_reply,
        dm: { ...base.dm, delivery: { text: base.dm.delivery.text, buttons: [] } },
        stats: { triggers: 12, contacts: 12, comment_replies_sent: 12, sends: 9, clicks: 3, ctr: null, emails: 0, reminders_sent: 0, failures: 0, timestamps: { last_triggered: ago(30) } }, timestamps: stamps(120, 40) });
      store().funnels[workout.id] = { comment_reply: { sent: 12, completed: 12 }, opening: { sent: 12, completed: 9 }, delivery: { sent: 9, completed: 3 } };
      const stories = templateByKey('story_leads', locale);
      const waitlist = add({ template_key: 'story_leads', name: copy.stories.name, status: 'paused', paused_reason: plan() === 'pro' ? 'creator' : 'plan_required', trigger: stories.draft.trigger, comment_reply: stories.draft.comment_reply,
        dm: { ...stories.draft.dm, delivery: { text: stories.draft.dm.delivery.text, buttons: [{ title: 'Lista', url: copy.stories.url }] } },
        stats: { triggers: 21, contacts: 20, comment_replies_sent: 0, sends: 14, clicks: 11, ctr: null, emails: 16, reminders_sent: 0, failures: 0, timestamps: { last_triggered: ago(200) } }, timestamps: stamps(400, 190) });
      store().funnels[waitlist.id] = { opening: { sent: 20, completed: 18 }, email_capture: { sent: 18, completed: 16 }, delivery: { sent: 14, completed: 11 } };
      for (let index = 0; index < 21; index += 1) {
        const outcome = index < 11 ? { status: 'completed', clicked: true, email: true } : index < 16 ? { status: 'completed', reached: 2, current: 'sent', email: true } : { status: 'expired', reached: 1, current: 'expired' };
        seedExecution(waitlist, index + 50, comments, 200 + index * 7, outcome);
      }
      const reply = templateByKey('dm_reply', locale);
      add({ template_key: 'dm_reply', name: copy.direct.name, status: 'draft', trigger: reply.draft.trigger, comment_reply: reply.draft.comment_reply, dm: reply.draft.dm, timestamps: stamps(3, 2, false) });
      add({ name: copy.trashed.name, status: 'paused', paused_reason: 'creator', trigger: { channel: 'comments', media_scope: 'all', media_ids: [], keywords: copy.trashed.keywords, match: 'word' }, dm: { ...base.dm, delivery: { text: base.dm.delivery.text, buttons: [] } }, timestamps: stamps(900, 72, true, 72) });
      sampleConversations().forEach(entry => seedConversation(profileId, entry, storyPhoto));
      // D3: without a plan, whatever was running stops until the creator subscribes.
      if (!quotaOf().available) store().automations.filter(item => item.profile_id === profileId && item.status === 'active').forEach(item => Object.assign(item, { status: 'paused', paused_reason: 'plan_required' }));
      if (mode === 'reauth') {
        const connection = connectionOf(profileId);
        Object.assign(connection, { status: 'reauthorization_required', capabilities: [], scopes: [], last_error: { code: 'token_expired', message: 'token_expired', timestamps: { occurred: ago(20) } }, version: connection.version + 1 });
        store().automations.filter(item => item.profile_id === profileId && item.status === 'active').forEach(item => Object.assign(item, { status: 'paused', paused_reason: 'connection_unavailable' }));
      }
    },
    // The worker's part when someone comments the keyword on a post: an execution through every
    // step, the Direct conversation and the counters.
    simulateTrigger: id => {
      const automation = untrashed(id);
      const locale = localeOf(automation.profile_id);
      const index = store().executions.filter(item => item.automation_id === id).length;
      const person = samplePerson(index + 40);
      const clicked = index % 2 === 0;
      const written = automation.trigger.keywords[0] || commentSamples(locale)[1];
      const execution = seedExecution(automation, index + 40, [written], 0, { status: clicked ? 'completed' : 'awaiting_reply', clicked, reached: clicked ? undefined : 1 });
      const stats = automation.stats;
      stats.triggers += 1;
      stats.contacts += 1;
      if (automation.comment_reply.enabled) stats.comment_replies_sent += 1;
      if (clicked) { stats.sends += 1; stats.clicks += 1; }
      stats.timestamps.last_triggered = now();
      const funnel = store().funnels[id] || (store().funnels[id] = {});
      execution.steps.forEach(step => {
        const entry = funnel[step.step] || (funnel[step.step] = { sent: 0, completed: 0 });
        if (step.status !== 'pending') entry.sent += 1;
        if (step.status === 'completed') entry.completed += 1;
      });
      const say = text => (text || '').replace(/\{\{username\}\}/g, `@${person.handle}`);
      let conversation = store().conversations.find(item => item.profile_id === automation.profile_id && item.participant.handle === execution.contact.handle);
      if (!conversation) {
        conversation = { id: nextId('scv'), profile_id: automation.profile_id, participant: { handle: execution.contact.handle, name: execution.contact.name, picture: null, contact_id: execution.contact.id }, unread_count: 0, created: now() };
        store().conversations.push(conversation);
      }
      if (automation.dm.opening.enabled) addMessage(conversation, { direction: 'outbound', origin: 'automation', automationId: id, text: say(automation.dm.opening.text) });
      if (clicked) {
        addMessage(conversation, { direction: 'inbound', origin: 'contact', text: automation.dm.opening.button_label || written });
        addMessage(conversation, { direction: 'outbound', origin: 'automation', automationId: id, text: say(automation.dm.delivery.text) });
        conversation.unread_count += 1;
      }
      touchAutomation(automation);
      return clone(execution);
    },
  };
  return { routes, server };
}
